import React from "react";
import { Link } from "react-router-dom";
import {
  Wrench, Zap, Home, Snowflake, Camera, HardHat, Truck, Smartphone, Bug, Video,
  Paintbrush, Plane, ShoppingBag, Car, FileText, Flag,
} from "lucide-react";
import StatusBadge from "@/components/StatusBadge";

const TONES = ["bg-teal-brand", "bg-indigo-500", "bg-rose-500", "bg-amber-500", "bg-cyan-600", "bg-purple-500", "bg-emerald-600"];

function iconFor(cat = "") {
  const c = cat.toLowerCase();
  if (c.includes("plumb")) return Wrench;
  if (c.includes("electric")) return Zap;
  if (c.includes("interior") || c.includes("clean")) return Home;
  if (c.includes("ac ") || c.includes("ac technician")) return Snowflake;
  if (c.includes("photo")) return Camera;
  if (c.includes("contractor") || c.includes("civil")) return HardHat;
  if (c.includes("moving") || c.includes("packing") || c.includes("delivery")) return Truck;
  if (c.includes("mobile") || c.includes("computer")) return Smartphone;
  if (c.includes("pest")) return Bug;
  if (c.includes("cctv")) return Video;
  if (c.includes("paint")) return Paintbrush;
  if (c.includes("travel")) return Plane;
  if (c.includes("seller") || c.includes("retail") || c.includes("shop")) return ShoppingBag;
  if (c.includes("auto") || c.includes("mechanic")) return Car;
  return FileText;
}

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return `${Math.max(1, Math.floor(d / 60))}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}

export default function DisputeCard({ c, onChallenge }) {
  const Icon = iconFor(c.primary_category || c.categories?.[0] || "");
  const tone = TONES[(c.author?.charCodeAt(0) || 0) % TONES.length];
  return (
    <Link to={`/dispute/${c.id}`} data-testid={`feed-card-${c.id}`}
      className="block rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-teal-brand/30 transition-all">
      <div className="p-4 flex items-start gap-3">
        <div className={`h-11 w-11 rounded-xl ${tone} text-white flex items-center justify-center shrink-0`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-slate-800">
            <span className="font-semibold">{c.author || "A user"}</span>
            <span className="text-slate-500"> filed a complaint {c.party_type === "about_vendor" ? "about a vendor" : "about a customer"}</span>
          </p>
          <p className="text-xs text-slate-400">
            <span className="font-medium text-slate-500">{c.primary_category || c.categories?.[0]}</span>
            {c.city ? ` · ${c.city}` : ""} · {timeAgo(c.created_at)} ago · <span className="font-mono">{c.complaint_code}</span>
          </p>
        </div>
        <StatusBadge status={c.status} />
      </div>
      <div className="px-4 pb-3">
        <p className="text-sm text-slate-700 leading-relaxed line-clamp-2">{c.summary}</p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(c.categories || []).map((x) => (
            <span key={x} className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[11px]">#{x.replace(/\s+/g, "")}</span>
          ))}
        </div>
      </div>
      <div className="px-4 py-2.5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
        <span>
          {c.responded ? <span className="text-purple-600 font-medium">● Other party responded</span> : <span className="text-amber-600 font-medium">● Awaiting response</span>}
          {c.settlement_recorded && <span className="text-emerald-600 font-medium ml-2">✓ Settlement recorded</span>}
        </span>
        <span className="inline-flex items-center gap-3">
          <span className="text-teal font-semibold">View dispute →</span>
          {onChallenge && (
            <button onClick={(e) => { e.preventDefault(); onChallenge(c); }} data-testid={`challenge-btn-${c.id}`}
              className="inline-flex items-center gap-1 text-slate-500 hover:text-teal">
              <Flag className="h-3.5 w-3.5" /> Challenge
            </button>
          )}
        </span>
      </div>
    </Link>
  );
}
